/**
 * Runs the unsaved draft once on the live integration, so the steps can be checked before the tool is
 * saved. Each step then shows how it ended. A draft with a write step asks first.
 */

import { useEffect, useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/lib/utils';
import { missingInputs } from '@/modules/back-testing/datasets/tools/tool-call';
import { ToolCallForm } from '@/modules/back-testing/datasets/tools/tool-call-form';
import { ConfirmWriteRun } from '@/modules/integrations/tool-test/confirm-write-run';
import { ToolRunResult } from '@/modules/integrations/tool-test/tool-run-result';
import type { ToolRunResult as ToolRunResultData } from '@/services/toolDefinition.service';
import { useCustomToolBuilder } from '../builder/builder-context';
import { draftToolAction } from '../draft-tool-action';
import { Note } from '../ui/note';
import { SidePanel } from '../ui/side-panel';
import { useTestCustomTool } from '../use-custom-tools';
import { outcomesOf } from './step-outcomes';

/** The run's result; a write answered with sample data (a tool server not in production mode) is said not to be sent. */
const DraftRunResult = ({ result, writes }: { result: ToolRunResultData; writes: boolean }) => {
	const simulated = Object.values(outcomesOf(result) ?? {}).some((outcome) => outcome.status === 'simulated');
	return (
		<>
			{simulated && (
				<Note>Writes were not sent: this tool server is not running in production mode, so it answers write steps with sample data. Reads ran for real.</Note>
			)}
			<ToolRunResult result={result} writes={writes && !simulated} />
		</>
	);
};

export const DraftTestPanel = () => {
	const { draft, stepTools, writes, buildIssue, testing, focusOn } = useCustomToolBuilder();
	const action = {
		...draftToolAction(draft, stepTools, writes),
		label: draft.name.trim() || 'This draft',
		description: draft.description.trim() || 'The unsaved steps of this draft.'
	};
	const [args, setArgs] = useState<Record<string, unknown>>({});
	const [confirming, setConfirming] = useState(false);
	const run = useTestCustomTool();
	const missing = missingInputs({ args }, action);

	// A result describes the steps it ran, and a confirmation the steps it showed; a changed draft starts over.
	const { reset } = run;
	useEffect(() => {
		reset();
		setConfirming(false);
	}, [draft.inputs, draft.steps, reset]);

	const send = () =>
		run.mutate(
			{ inputs: draft.inputs, steps: draft.steps, input: args },
			{ onSuccess: (result) => testing.setOutcomes(outcomesOf(result)) }
		);

	return (
		<SidePanel label='Test this draft' onClose={() => testing.setOpen(false)}>
			<header className='border-b border-gray-500 py-3 pr-12 pl-4'>
				<h2 className='text-[13px] leading-4.5 font-medium text-ink'>Test this draft</h2>
				<p className='text-xs leading-4 text-gray-800'>
					{writes
						? 'Runs every step once on your live integration and changes data. Nothing is saved.'
						: 'Runs every step once on your live integration. It only reads, and nothing is saved.'}
				</p>
			</header>
			{buildIssue ? (
				<div className='flex flex-col gap-2 p-4'>
					<Note>{`Finish the steps first: ${buildIssue.message}.`}</Note>
					{buildIssue.at && (
						<button
							type='button'
							className='self-start text-xs leading-4 text-indigo-700 hover:underline'
							onClick={() => focusOn(buildIssue.at ?? null)}
						>
							{buildIssue.at.kind === 'step' ? 'Show that step' : 'Show the inputs'}
						</button>
					)}
				</div>
			) : (
				<div className='flex flex-col gap-4 p-4'>
					<div className='@container flex flex-col overflow-hidden rounded-xl border border-gray-500'>
						{draft.inputs.length ? (
							<ToolCallForm definition={action} sharedBy={[]} args={args} onChange={setArgs} />
						) : (
							<p className='px-4 py-3 text-xs leading-4 text-gray-800'>This tool takes no inputs from the AI.</p>
						)}
						<div className='flex items-center justify-end gap-3 border-t border-gray-500 px-4 py-3'>
							{missing.length > 0 && <span className='text-xs text-red-600'>{`Fill in ${missing.join(', ')} to run.`}</span>}
							<Button size='sm' disabled={missing.length > 0 || run.isPending} onClick={() => (writes ? setConfirming(true) : send())}>
								{run.isPending ? <Loader2 className='size-3.5 animate-spin' /> : <Play className='size-3.5' />}
								Run
							</Button>
						</div>
					</div>
					{run.isError && (
						<p role='alert' className='text-[13px] leading-4.5 text-red-600'>
							{getApiErrorMessage(run.error, 'The run could not start.')}
						</p>
					)}
					{run.data && !run.isPending && <DraftRunResult result={run.data} writes={writes} />}
				</div>
			)}
			<ConfirmWriteRun open={confirming} label={action.label} onOpenChange={setConfirming} onConfirm={send} />
		</SidePanel>
	);
};
